import { MAY_2025 } from "components/Screens/Employee/constant";

import { getDateFormatForPeru } from "helpers";

const parseCurrencyValue = (currencyString) => {
  if (!currencyString) return 0;
  return parseFloat(currencyString.replace(/[$,S/\s]/g, "")) || 0;
};

const parseFullName = (fullName) => {
  if (!fullName) return { firstName: "", lastName: "" };
  const nameParts = fullName.trim().split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";
  return { firstName, lastName };
};

export const mapPeruPayslipData = (payslipData) => {
  if (!payslipData) return null;

  const { General, Summary } = payslipData || {};

  const {
    Name,
    DNI,
    City,
    "Date of joining": joiningDate,
    "Job title": jobTitle,
    Department,
    Category,
    "Special situation": specialSituation,
    "Health System": healthSystem,
    ESSAL,
    "AFP/ONP": afpOnp,
    CUSSP,
  } = General || {};

  const personalInformation = [
    { item: "Name", value: Name || "N/A" },
    { item: "DNI", value: DNI || "N/A" },
    { item: "City", value: City || "N/A" },
    { item: "Date of joining", value: getDateFormatForPeru(joiningDate) || "N/A" },
    { item: "Job title", value: jobTitle || "N/A" },
    { item: "Department", value: Department || "N/A" },
    { item: "Category", value: Category || "N/A" },
    { item: "Special situation", value: specialSituation || "N/A" },
    { item: "Health system", value: healthSystem || "N/A" },
    { item: "ESSAL", value: ESSAL || "N/A" },
    { item: "AFP/ONP", value: afpOnp || "N/A" },
    { item: "CUSSP", value: CUSSP || "N/A" },
  ];

  const isGratification = payslipData?.payslipType === "Gratification";

  const salaryInformation = isGratification
    ? [
        { item: "Salary per hour", value: parseCurrencyValue(Summary?.["Salary Per Hour"]) || "N/A" },
        { item: "Salary per day", value: parseCurrencyValue(Summary?.["Salary Per Day"]) || "N/A" },
        { item: "Salary per month", value: parseCurrencyValue(General?.["Basic Salary"]) || "N/A" },
        { item: "Days worked", value: General?.["Days Worked"] || "N/A" },
        { item: "Unpaid Leaves", value: General?.["Unpaid Leaves"] ?? "N/A" },
        { item: "Period", value: General?.Period || "N/A" },
        { item: "Bank Name", value: General?.["Bank Name"] || "N/A" },
        { item: "Bank Account Number", value: General?.["Account Number"] || "N/A" },
      ]
    : [
        { item: "Salary per hour", value: parseCurrencyValue(Summary?.["Salary Per Hour"]) || "N/A" },
        { item: "Salary per day", value: parseCurrencyValue(Summary?.["Salary Per Day"]) || "N/A" },
        { item: "Salary per month", value: parseCurrencyValue(General?.["Basic Salary"]) || "N/A" },
        { item: "Days worked", value: General?.["Days Worked"] || "N/A" },
        { item: "Overtime Hours", value: General?.["Overtime Hours"] || "N/A" },
        { item: "Vacation Days", value: General?.["Vacation Days"] || "N/A" },
        { item: "Bank Name", value: General?.["Bank Name"] || "N/A" },
        { item: "Bank Account Number", value: General?.["Account Number"] || "N/A" },
      ];

  const extractCurrentAndYtd = (value) => {
    if (value === null || value === undefined) return { current: 0, ytd: 0 };
    if (typeof value === "object") return { current: value?.current, ytd: value?.ytd };
    return { current: value, ytd: 0 };
  };

  const mapPayslipItems = (items, data, keyMapper = (key) => key) => {
    Object.entries(data).forEach(([key, value]) => {
      const { current, ytd } = extractCurrentAndYtd(value);
      items.push({
        item: keyMapper(key),
        value: parseCurrencyValue(current),
        ytd: parseCurrencyValue(ytd),
      });
    });
  };

  const benefitsInformation = [];
  if (payslipData?.Benefit) {
    mapPayslipItems(benefitsInformation, payslipData?.Benefit, (key) =>
      key === "Life Inusrance" ? "Private health insurance" : key
    );
    const { current: totalBenefitsCurrent, ytd: totalBenefitsYtd } = extractCurrentAndYtd(
      payslipData?.Aggregate?.["Total Benefits"]
    );
    benefitsInformation.push({
      item: "Total",
      value: parseCurrencyValue(totalBenefitsCurrent),
      ytd: parseCurrencyValue(totalBenefitsYtd),
    });
  }

  const incomesInformation = [];
  if (payslipData?.Addition) {
    mapPayslipItems(incomesInformation, payslipData?.Addition);
    const totalIncomesValue = isGratification
      ? payslipData?.Aggregate?.["Total Incomes"]
      : payslipData?.Aggregate?.["Total Additions"];
    const { current: totalIncomesCurrent, ytd: totalIncomesYtd } = extractCurrentAndYtd(totalIncomesValue);
    incomesInformation.push({
      item: "Total Incomes",
      value: parseCurrencyValue(totalIncomesCurrent),
      ytd: parseCurrencyValue(totalIncomesYtd),
    });
  }

  const deductionsInformation = [];
  if (payslipData?.Deduction) {
    mapPayslipItems(deductionsInformation, payslipData?.Deduction);
    const { current: totalDeductionsCurrent, ytd: totalDeductionsYtd } = extractCurrentAndYtd(
      payslipData?.Aggregate?.["Total Deductions"]
    );
    deductionsInformation.push({
      item: "Total",
      value: parseCurrencyValue(totalDeductionsCurrent),
      ytd: parseCurrencyValue(totalDeductionsYtd),
    });
  }

  const summary = {
    currentMonth: {
      totalIncomes: parseCurrencyValue(payslipData?.Summary?.["Total Incomes"]),
      totalDeductions: parseCurrencyValue(payslipData?.Summary?.["Total Deductions"]),
      netPayment: parseCurrencyValue(payslipData?.Summary?.["Net Amount"]),
    },
  };

  return {
    personalInformation,
    salaryInformation,
    benefitsInformation,
    incomesInformation,
    deductionsInformation,
    summary,
    payslipMonth: payslipData.payslipMonthFormated || "",
    payslipType: payslipData.payslipType || "",
  };
};

export const mapNewPayslipDataToCurrentFormat = (newFormatData) => {
  if (!newFormatData) return null;

  const { firstName, lastName } = parseFullName(newFormatData.General?.Name);
  const allowances = [];
  const additionData = newFormatData.Addition || {};

  Object.entries(additionData).forEach(([key, value]) => {
    allowances.push({
      name: key,
      amount: parseCurrencyValue(value?.current),
      ytd: parseCurrencyValue(value?.ytd),
    });
  });

  const deductions = [];
  const deductionData = newFormatData.Deduction || {};

  Object.entries(deductionData).forEach(([key, value]) => {
    deductions.push({
      name: key,
      amount: parseCurrencyValue(value?.current),
      ytd: parseCurrencyValue(value?.ytd),
    });
  });

  const transformedData = {
    user: {
      firstName,
      lastName,
      jobTitle: newFormatData.General?.["Job title"] || "",
      employmentType: newFormatData.General?.["Employment Type"] || "",
      department: newFormatData.General?.Department || "",
      id: newFormatData.employeeId || "",
      joiningDate: newFormatData.General?.["Date of joining"] || null,
      DOB: newFormatData.General?.DOB || null,
    },
    payroll: {
      monthlySalary: parseCurrencyValue(newFormatData.General?.["Basic Salary"]),
      netAmount: parseCurrencyValue(newFormatData.Summary?.["Net Amount"]),
      account: null,
      bank: null,
      month: newFormatData.payslipMonth,
      id: newFormatData.id,
    },
    allowance: allowances,
    deductions,
    total: {
      netAmount: parseCurrencyValue(newFormatData.Summary?.["Net Amount"]),
      totalAllowance: parseCurrencyValue(newFormatData.Aggregate?.["Total Additions"]?.current),
      totalAllowanceYTD: parseCurrencyValue(newFormatData.Aggregate?.["Total Additions"]?.ytd),
      totalDeductions: parseCurrencyValue(newFormatData.Aggregate?.["Total Deductions"]?.current),
      totalDeductionsYTD: parseCurrencyValue(newFormatData.Aggregate?.["Total Deductions"]?.ytd),
    },
  };

  return transformedData;
};

export const getFormattedPayslipData = (payslipMonth, payslipData) => {
  if (!payslipMonth || !payslipData) return payslipData;

  const payslipDate = new Date(payslipMonth);

  if (payslipData.country?.toLowerCase() === "peru") {
    return mapPeruPayslipData(payslipData);
  }

  if (payslipDate >= MAY_2025) {
    return mapNewPayslipDataToCurrentFormat(payslipData);
  }

  return payslipData;
};

export default {
  mapNewPayslipDataToCurrentFormat,
  mapPeruPayslipData,
  getFormattedPayslipData,
};
